/**
 * Logika koła fortuny (PLAN §10.2) — czysta, testowalna.
 * Wynik tier pochodzi z serwera; animacja tylko prezentuje.
 */
import { FORTUNE_GRANTS, type FortuneTier } from '@pieskowo/shared';
import { scopedKeyIn } from '../../core/profileScope.js';

export const FORTUNE_TIERS: readonly FortuneTier[] = ['bogaty', 'normalny', 'biedny'] as const;

/** Kolejność segmentów na kole (zgodnie z ruchem wskazówek zegara od góry). */
export const WHEEL_SEGMENTS: readonly FortuneTier[] = [
  'bogaty',
  'normalny',
  'biedny',
  'bogaty',
  'normalny',
  'biedny',
] as const;

export type TierPresentation = {
  tier: FortuneTier;
  labelPl: string;
  emoji: string;
  messagePl: string;
  grantZl: number;
  color: number;
};

export function tierPresentation(tier: FortuneTier): TierPresentation {
  const grantZl = FORTUNE_GRANTS[tier];
  if (tier === 'bogaty') {
    return {
      tier,
      labelPl: 'Skarb!',
      emoji: '💎',
      messagePl: `Uuu, skarb! +${grantZl} zł`,
      grantZl,
      color: 0xffd700,
    };
  }
  if (tier === 'normalny') {
    return {
      tier,
      labelPl: 'Świetny początek!',
      emoji: '⭐',
      messagePl: `Świetny początek! +${grantZl} zł`,
      grantZl,
      color: 0xb8e6d0,
    };
  }
  return {
    tier: 'biedny',
    labelPl: 'Kiełki',
    emoji: '🌱',
    messagePl: `Uzbierasz sama! Startujesz z ${grantZl} zł`,
    grantZl,
    color: 0xbde0fe,
  };
}

/** Kąt docelowy (stopnie, Phaser: 0 = w prawo, clockwise) by wskaźnik u góry trafił w tier. */
export function targetAngleForTier(tier: FortuneTier, extraSpins = 4): number {
  const indices = WHEEL_SEGMENTS.map((t, i) => (t === tier ? i : -1)).filter((i) => i >= 0);
  const pick = indices[Math.floor(indices.length / 2)] ?? 0;
  const seg = 360 / WHEEL_SEGMENTS.length;
  // środek segmentu; wskaźnik na górze = -90° w układzie Phaser relative do 0=right
  const mid = pick * seg + seg / 2;
  // obrót koła tak, by mid był pod wskaźnikiem (góra = 270° lub -90°)
  const base = 270 - mid;
  return base + extraSpins * 360;
}

const IDEM_KEY = 'pieskowo.fortuneIdempotencyKey';
const RESULT_KEY = 'pieskowo.fortuneLastResult';

export function getOrCreateFortuneIdempotencyKey(
  storage: Storage = typeof localStorage !== 'undefined' ? localStorage : memoryStorage(),
): string {
  const key = scopedKeyIn(IDEM_KEY, storage);
  try {
    const existing = storage.getItem(key);
    if (existing && isUuid(existing)) return existing;
    const fresh = cryptoRandomUuid();
    storage.setItem(key, fresh);
    return fresh;
  } catch {
    return cryptoRandomUuid();
  }
}

export function clearFortuneIdempotencyKey(
  storage: Storage = typeof localStorage !== 'undefined' ? localStorage : memoryStorage(),
): void {
  try {
    storage.removeItem(scopedKeyIn(IDEM_KEY, storage));
  } catch {
    /* ignore */
  }
}

export function saveFortuneResultLocal(
  result: { tier: FortuneTier; grantZl: number; balance: number },
  storage: Storage = typeof localStorage !== 'undefined' ? localStorage : memoryStorage(),
): void {
  try {
    storage.setItem(scopedKeyIn(RESULT_KEY, storage), JSON.stringify(result));
  } catch {
    /* ignore */
  }
}

export function loadFortuneResultLocal(
  storage: Storage = typeof localStorage !== 'undefined' ? localStorage : memoryStorage(),
): { tier: FortuneTier; grantZl: number; balance: number } | null {
  try {
    const raw = storage.getItem(scopedKeyIn(RESULT_KEY, storage));
    if (!raw) return null;
    const v = JSON.parse(raw) as { tier?: string; grantZl?: number; balance?: number };
    if (v.tier === 'bogaty' || v.tier === 'normalny' || v.tier === 'biedny') {
      return {
        tier: v.tier,
        grantZl: typeof v.grantZl === 'number' ? v.grantZl : FORTUNE_GRANTS[v.tier],
        balance: typeof v.balance === 'number' ? v.balance : FORTUNE_GRANTS[v.tier],
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** Offline / stub: ten sam klucz → ten sam tier (idempotencja lokalna). */
export function offlineSpin(
  idempotencyKey: string,
  previous?: { tier: FortuneTier; grantZl: number; balance: number } | null,
): { tier: FortuneTier; grantZl: number; balance: number } {
  if (previous) return previous;
  // deterministyczny hash klucza → tier (wagi ~25/50/25)
  let h = 0;
  for (let i = 0; i < idempotencyKey.length; i++) {
    h = (Math.imul(31, h) + idempotencyKey.charCodeAt(i)) | 0;
  }
  const r = Math.abs(h % 100) / 100;
  const tier: FortuneTier = r < 0.25 ? 'bogaty' : r < 0.75 ? 'normalny' : 'biedny';
  const grantZl = FORTUNE_GRANTS[tier];
  return { tier, grantZl, balance: grantZl };
}

function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    s,
  );
}

function cryptoRandomUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // fallback RFC4122 v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function memoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear() {
      map.clear();
    },
    getItem(k: string) {
      return map.has(k) ? map.get(k)! : null;
    },
    key(i: number) {
      return [...map.keys()][i] ?? null;
    },
    removeItem(k: string) {
      map.delete(k);
    },
    setItem(k: string, v: string) {
      map.set(k, String(v));
    },
  };
}
